/**
 * Servicio de WebSocket — AWS API Gateway
 * Conecta al WebSocket de AWS con JWT de Cognito.
 * El servidor detecta admins automáticamente via grupos en el JWT.
 */

import { API_CONFIG, WEBSOCKET_CONFIG } from '../constants/config';
import { fetchAuthSession } from 'aws-amplify/auth';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectTimeout = null;
    this.reconnectAttempts = 0;
    this.messageHandlers = new Map();
    this.isIntentionallyClosed = false;
  }

  /**
   * Obtiene el ID token JWT de Cognito.
   * @private
   */
  async _getJWT() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  /**
   * Conecta al WebSocket de AWS API Gateway
   * @returns {Promise<void>}
   */
  async connect() {
    try {
      const jwt = await this._getJWT();
      if (!jwt) {
        throw new Error('No se pudo obtener JWT para WebSocket');
      }

      const wsUrl = `${API_CONFIG.WS_URL}?token=${jwt}`;

      return new Promise((resolve, reject) => {
        this.ws = new WebSocket(wsUrl);
        this.isIntentionallyClosed = false;

        this.ws.onopen = () => {
          console.log('✅ WebSocket conectado a AWS API Gateway');
          this.reconnectAttempts = 0;
          // Request initial device list after connection is established
          this._requestDeviceList();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this._handleMessage(event);
        };

        this.ws.onerror = (error) => {
          console.error('❌ Error en WebSocket:', error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log(`WebSocket cerrado: code=${event.code} reason=${event.reason}`);
          if (!this.isIntentionallyClosed) {
            this._handleReconnect();
          }
        };
      });
    } catch (error) {
      console.error('Error al conectar WebSocket:', error);
      throw error;
    }
  }

  /**
   * Solicita la lista inicial de dispositivos al servidor
   * @private
   */
  _requestDeviceList() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'get_devices' }));
      console.log('📋 Solicitando lista de dispositivos...');
    }
  }

  /**
   * Maneja la reconexión automática (renueva JWT en cada intento)
   * @private
   */
  _handleReconnect() {
    if (this.reconnectAttempts >= WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS) {
      console.error('Máximo de intentos de reconexión alcanzado');
      return;
    }

    this.reconnectAttempts++;

    console.log(`🔄 Reconectando (intento ${this.reconnectAttempts})...`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(console.error);
    }, WEBSOCKET_CONFIG.RECONNECT_INTERVAL);
  }

  /**
   * Procesa mensajes recibidos
   * @private
   */
  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Ejecutar handlers registrados para este tipo de mensaje
      const handlers = this.messageHandlers.get(data.type) || [];
      handlers.forEach(handler => handler(data));

      // Ejecutar handlers globales (tipo '*')
      const globalHandlers = this.messageHandlers.get('*') || [];
      globalHandlers.forEach(handler => handler(data));
    } catch (error) {
      console.error('Error parseando mensaje:', error);
    }
  }

  /**
   * Registra un handler para un tipo de mensaje
   * @param {string} messageType - Tipo de mensaje o '*' para todos
   * @param {Function} handler - Función callback
   */
  on(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType).push(handler);
  }

  /**
   * Elimina un handler
   * @param {string} messageType - Tipo de mensaje
   * @param {Function} handler - Función callback a eliminar
   */
  off(messageType, handler) {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Envía un mensaje al servidor (route $default)
   * @param {Object} data - Datos a enviar
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('⚠️ WebSocket no está conectado');
    }
  }

  /**
   * Cierra la conexión
   */
  disconnect() {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Limpiar handlers
    this.messageHandlers.clear();
  }

  /**
   * Verifica si está conectado
   * @returns {boolean}
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Exportar como singleton
export default new WebSocketService();
